// MACHINERY V2 (Stroje) — spec-sheet classification + description resolution
// (M2.5).
//
// Spec: data package §3, the "Data-parity (miss any → broken tab)" paragraph.
// This is components/MachineSpecs.tsx's `useMemo` body (its lines 918-1044 at
// c15fb351) lifted out of React and made pure, for the reason given in the
// specSheet.ts header: that file imports MUI, so v2 cannot import from it.
//
// THE THREE BUCKETS, in v1's order, unchanged:
//   description — a name in DESCRIPTION_SECTIONS, OR the >50%-empty-specKey
//                 heuristic that catches machine-type headings not in the set.
//   equipment   — "standard equipment" / "optional equipment".
//   regular     — everything else.
// Each bucket is then stable-sorted by getSectionOrder. Sections with zero specs
// are dropped BEFORE classification, exactly as v1 filters them.
//
// THE LANGUAGE RULE, unchanged and easy to get wrong:
//   Descriptions arrive in two shapes — (A) the section NAME carries the tag
//   ("Description (EN)" / "Description (CZ)"), or (B) a section named
//   "Description" whose SPEC KEYS carry it. Text is harvested from both.
//   · lang "sk" → prefer CZ; fall back to EN when CZ is empty OR mojibake, and
//     flag the fallback ONLY when there WAS Czech text and it was garbled (an
//     absent CZ is not a fallback worth badging).
//   · lang "en" → EN only. NEVER fall back to CZ.
//   Machine-type description sections are then filtered: mojibake is dropped in
//   every language, and Czech-looking prose is additionally dropped for "en".
import { CatalogSpecItem, CatalogSpecSection } from "../../../models/models";
import {
  getSectionOrder,
  hasMojibake,
  isCzechText,
  isDescriptionLikeSection,
  isDescriptionSection,
  isEquipmentSection,
} from "./specSheet";

export interface MachineTypeDescription {
  label: string;
  text: string;
}

export interface ResolvedSpecSheet {
  primaryText: string;
  machineTypeDescriptions: MachineTypeDescription[];
  regularSections: CatalogSpecSection[];
  equipmentSections: CatalogSpecSection[];
  /** True when Slovak was asked for, the Czech text was garbled, and English
   *  was shown instead — the "EN" badge in the description header. */
  usedEncodingFallback: boolean;
}

const specsToText = (specs: CatalogSpecItem[]): string =>
  specs
    .map((s) => s.specValue)
    .filter(Boolean)
    .join("\n\n")
    .trim();

const byOrder = (a: CatalogSpecSection, b: CatalogSpecSection): number =>
  getSectionOrder(a.sectionName) - getSectionOrder(b.sectionName);

export const resolveSpecSheet = (
  sections: CatalogSpecSection[] | undefined,
  currentLang: string
): ResolvedSpecSheet => {
  const allSections = (sections || []).filter((s) => s.specs.length > 0);

  const desc: CatalogSpecSection[] = [];
  const regular: CatalogSpecSection[] = [];
  const equip: CatalogSpecSection[] = [];

  for (const section of allSections) {
    if (isDescriptionSection(section.sectionName) || isDescriptionLikeSection(section)) {
      desc.push(section);
    } else if (isEquipmentSection(section.sectionName)) {
      equip.push(section);
    } else {
      regular.push(section);
    }
  }

  desc.sort(byOrder);
  regular.sort(byOrder);
  equip.sort(byOrder);

  // Format A — the language tag is in the section name.
  const enSectionA = desc.find((s) => s.sectionName.toLowerCase() === "description (en)");
  const czSectionA = desc.find((s) => s.sectionName.toLowerCase() === "description (cz)");

  // Format B — a generic "Description" section whose spec KEYS carry the tag.
  const generic = desc.find((s) => s.sectionName.toLowerCase() === "description");
  const enSpecsB = generic
    ? generic.specs.filter((s) => s.specKey?.toLowerCase().includes("(en)"))
    : [];
  const czSpecsB = generic
    ? generic.specs.filter((s) => s.specKey?.toLowerCase().includes("(cz)"))
    : [];

  const enText = specsToText(enSectionA?.specs || []) || specsToText(enSpecsB);
  const czText = specsToText(czSectionA?.specs || []) || specsToText(czSpecsB);
  const czHasMojibake = czText ? hasMojibake(czText) : false;

  let primaryText = "";
  let usedEncodingFallback = false;
  if (currentLang === "sk") {
    if (czHasMojibake || !czText) {
      primaryText = enText;
      usedEncodingFallback = czHasMojibake && enText.length > 0;
    } else {
      primaryText = czText;
    }
  } else {
    primaryText = enText;
  }

  const machineTypeDescriptions = desc
    .filter((s) => {
      const lower = s.sectionName.toLowerCase();
      return (
        lower !== "description (en)" && lower !== "description (cz)" && lower !== "description"
      );
    })
    .map((s) => ({ label: s.sectionName, text: specsToText(s.specs) }))
    .filter((d) => {
      if (d.text.length === 0) return false;
      if (hasMojibake(d.text)) return false;
      if (currentLang === "en" && isCzechText(d.text)) return false;
      return true;
    });

  return {
    primaryText,
    machineTypeDescriptions,
    regularSections: regular,
    equipmentSections: equip,
    usedEncodingFallback,
  };
};
